import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ENGINE_LABELS } from "@/config";
import { Page, PageHeader, Card, StatusBadge, PrivacyNote, Loading, usd, shortDate, ago } from "@/components/common";
import { TextScramble } from "@/components/Motion";
import { EngineWithLogo } from "@/components/ProviderLogo";
import { Button } from "@/components/ui/button";
import { Search, Copy, ShieldCheck } from "lucide-react";

// design_handoff_crewbench/screens/Admin Sessions.dc.html: find where a session runs and where its data lives
// (backend/support_routes.py: by short or full ID, branch, backup path or container name).
const when = (iso) => (iso ? shortDate(iso, true) : "—");
const size = (n) => (n == null ? "" : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);

function copy(value) {
  navigator.clipboard.writeText(value).then(() => toast.success("Copied."), () => toast.error("Copy failed. Select it and copy it."));
}

// a label/value row on the 150px grid; code values are mono with a copy button
function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-[150px_minmax(0,1fr)_32px] gap-3 items-center py-2 border-t text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono [overflow-wrap:anywhere] min-w-0" : "text-base [overflow-wrap:anywhere] min-w-0"}>{value}</span>
      {mono ? <Button size="sm" variant="ghost" className="w-7 px-0 text-muted-foreground hover:text-foreground" aria-label={`Copy ${label.toLowerCase()}`} title="Copy"
        onClick={() => copy(value)}><Copy /></Button> : <span />}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <Card className="px-5 py-4 flex flex-col">
      <span className="font-display text-[15px] tracking-[0.02em] font-semibold pb-2">{title}</span>
      {children}
    </Card>
  );
}

function Details({ s }) {
  const pr = s.pull_request;
  const backup = s.home_backup;
  const runner = s.runner;
  return (
    <div className="flex flex-col gap-4" data-testid="admin-session-details">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-display text-[22px] leading-[1.25] tracking-[0.02em] font-semibold">Session <TextScramble>{s.short_id}</TextScramble></span>
        <StatusBadge status={s.status} live />
        <span className="text-sm text-muted-foreground">{[s.coder?.name, s.company?.name].filter(Boolean).join(" · ")}</span>
      </div>
      <Group title="Who and what">
        <Row label="Session ID" value={s.id} mono />
        <Row label="Company" value={s.company?.name || "—"} />
        <Row label="Person" value={s.coder ? `${s.coder.name} · ${s.coder.email}` : "—"} />
        <Row label="Project" value={s.project ? [s.project.name, s.project.github_repo].filter(Boolean).join(" · ") : "—"} />
        <Row label="Branch" value={s.branch} mono />
        <Row label="Pull request" value={pr?.url ? <a href={pr.url} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:opacity-50">#{pr.number}, {pr.status}</a>
          : pr?.status ? `${pr.status}${pr.reason ? `: ${pr.reason}` : ""}` : "None yet"} />
        <Row label="Engine" value={`${ENGINE_LABELS[s.engine] || s.engine} · ${s.model}`} />
        <Row label="Budget" value={`${usd(s.spent_usd)} of ${usd(s.budget_cap_usd)}`} />
        <Row label="Created" value={when(s.created_at)} />
        <Row label="Last active" value={when(s.last_activity_at)} />
        {s.ended_at && <Row label="Ended" value={when(s.ended_at)} />}
      </Group>
      <Group title="Where it lives">
        <Row label="Runner" value={runner ? `${runner.name}${runner.version ? ` · ${runner.version}` : ""} · ${runner.connected ? "online" : "offline"}` : "—"} />
        <Row label="Backup folder" value={s.where.r2} mono />
        <Row label="Last backup" value={s.home_backup_purged_at ? `Deleted ${when(s.home_backup_purged_at)} (30 days after the end)`
          : backup ? `${when(backup.at)} · ${size(backup.size)}` : "None yet"} />
        <Row label="Home" value={s.where.runner_home} mono />
        <Row label="Worktree" value={s.where.worktree} mono />
        <Row label="Local archive" value={s.where.local_archive} mono />
        <Row label="Session container" value={s.where.containers[0]} mono />
        <Row label="Network sidecar" value={s.where.containers[1]} mono />
        <Row label="Network" value={s.where.network} mono />
        <span className="pt-2 text-sm text-muted-foreground">Runner paths are under its RUNNER_HOME (/var/lib/crewbench with install.sh).</span>
      </Group>
    </div>
  );
}

export default function Sessions() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [text, setText] = useState(q);
  useEffect(() => setText(q), [q]); // back and forward
  // search as you type, from 3 characters
  useEffect(() => {
    const t = text.trim();
    if (t === q || (t && t.length < 3)) return undefined;
    const timer = setTimeout(() => setParams(t ? { q: t } : {}, { replace: true }), 300);
    return () => clearTimeout(timer);
  }, [text]);  // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading } = useQuery({ queryKey: ["admin-sessions", q], queryFn: async () => (await api.get("/admin/sessions", { params: { q } })).data });
  const sessions = data?.sessions || [];
  const short = text.trim() && text.trim().length < 3;
  return (
    <Page className="gap-5" data-testid="admin-sessions">
      <PageHeader eyebrow="Platform · Sessions" title="Find where a session runs and where its data lives."
        status="Search by short ID, full ID, branch, backup folder or container." />
      <form className="flex items-center gap-2 max-w-[560px] h-9 px-3 rounded-md border border-gr-control-border focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring"
        onSubmit={(e) => { e.preventDefault(); setParams(text.trim() ? { q: text.trim() } : {}); }}>
        <Search className="w-4 h-4 shrink-0 text-muted-foreground" />
        <input type="search" aria-label="Find a session" value={text} onChange={(e) => setText(e.target.value)} placeholder="353df3, s/cody/353df3 or crewbench-ses-…"
          className="flex-1 min-w-0 border-0 bg-transparent font-mono text-[12px] outline-none placeholder:text-muted-foreground" data-testid="admin-sessions-search" />
      </form>
      {short ? <Card className="px-5 py-6 text-muted-foreground">Type at least 3 characters.</Card>
        : isLoading ? <Loading />
        : q && sessions.length === 0 ? <Card className="px-5 py-6 text-muted-foreground" data-testid="admin-sessions-empty">No session matches. Check the ID, or try the branch or a container name.</Card>
        : q && sessions.length === 1 ? <Details s={sessions[0]} />
        : sessions.length === 0 ? <Card className="px-5 py-6 text-muted-foreground" data-testid="admin-sessions-empty">No sessions yet.</Card>
        : (
          <div className="flex flex-col gap-2">
            <span className="text-sm text-muted-foreground">{q ? `${sessions.length} sessions match. Pick one.` : "The most recently active sessions."}</span>
            <div className="bg-card rounded-[10px] overflow-x-auto text-sm tabular-nums">
              {sessions.map((s) => (
                <button key={s.id} type="button" onClick={() => setParams({ q: s.id })} data-testid={`admin-session-row-${s.short_id}`}
                  className="w-full min-w-[760px] text-left grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_180px_96px_90px] gap-3.5 items-center px-4 py-2.5 border-b last:border-b-0 hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring">
                  <span className="text-base font-semibold"><TextScramble>{s.short_id}</TextScramble></span>
                  <span className="truncate">{s.company?.name || "—"}</span>
                  <span className="truncate">{s.coder?.name || "—"}</span>
                  <span className="truncate text-muted-foreground">{s.project?.name || "—"}</span>
                  <EngineWithLogo engine={s.engine} className="gap-1.5 [&_svg]:w-3.5 [&_svg]:h-3.5" />
                  <StatusBadge status={s.status} live />
                  <span className="text-right text-muted-foreground">{ago(s.last_activity_at)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      <PrivacyNote icon={ShieldCheck}>This shows where a session's data is, not what it says. Its home and backups are encrypted with the session's key, and nobody here can read its prompts or replies.</PrivacyNote>
    </Page>
  );
}
